import { PropsWithChildren, useContext } from 'react'
import { BuildingLibraryIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { useForm } from 'react-hook-form'
import { Country } from 'react-phone-number-input'
import {
	Button,
	FormSubsection,
	Header,
	PayoutDetailsFields,
	PhoneNumberInput,
	SecurityCard
} from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { AuthContext } from 'store/AuthContext'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { cn } from 'utils/style'
import { useEditUserValidationSchema } from './hooks/useEditUserValidationSchema'
import { getProfileFormValues, usePopulateProfile } from './hooks/usePopulateProfile'
import { useEditProfileMutation } from './mutations/useEditProfileMutation'

type ProfileSectionProps = PropsWithChildren<{
	title: string
	isFirst?: boolean
}>

const ProfileSection = ({ title, isFirst = false, children }: ProfileSectionProps) => (
	<section className={cn('space-y-5', !isFirst && 'border-t border-border pt-8')}>
		<div className="flex items-center gap-3">
			<span className="h-5 w-1 rounded-full bg-primary" />
			<h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
		</div>
		<div className="grid gap-4 sm:grid-cols-2">{children}</div>
	</section>
)

const ProfilePage = () => {
	const { t } = useTranslation()
	const settingsCtx = useContext(StoreSettingsContext)
	const { auth } = useContext(AuthContext)
	const isIceland = settingsCtx?.storeSettings?.country == 'IS'
	const validationResolver = useEditUserValidationSchema()

	const form = useForm({ mode: 'onChange', resolver: validationResolver })
	const {
		register,
		handleSubmit,
		reset,
		control,
		setError,
		formState: { errors }
	} = form
	const fieldErrors = errors as any

	usePopulateProfile({
		data: auth?.user,
		reset
	})

	const { editProfileMutation, isEditProfileLoading } = useEditProfileMutation(setError)

	const onSubmit = (data: any) => {
		editProfileMutation(data, { onSuccess: (res) => reset(getProfileFormValues(res.data)) })
	}

	return (
		<>
			<Header eyebrow={t('Account')} title={t('Profile')} />
			<div className="mx-auto grid max-w-[960px] items-start gap-5 px-5 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)] lg:gap-6 lg:px-0">
				<form
					className="space-y-8 rounded-lg border border-border bg-card p-5 shadow-panel sm:p-7 lg:p-8 [&_.PhoneInput]:bg-card [&_input:read-only]:bg-muted [&_input:read-only]:text-muted-foreground [&_input]:bg-card [&_label]:text-[11px] [&_label]:uppercase [&_label]:text-muted-foreground"
					onSubmit={handleSubmit(onSubmit)}>
					<ProfileSection title={t('Account')} isFirst>
						<InputGroup
							id="email"
							type="email"
							className="sm:col-span-2"
							label={t('Email')}
							readOnly
							{...register('email')}
							error={fieldErrors.email}
						/>
						{isIceland && (
							<InputGroup
								id="ssn"
								className="sm:col-span-2"
								label={t('SSN')}
								{...register('ssn')}
								error={fieldErrors.ssn}
							/>
						)}
						<FormSubsection
							title={t('Payout details')}
							description={t('Where payouts are sent.')}
							icon={<BuildingLibraryIcon />}
							tone="danger"
						/>
						<PayoutDetailsFields form={form} />
					</ProfileSection>

					<ProfileSection title={t('Personal')}>
						<InputGroup
							id="name"
							className="sm:col-span-2"
							label={t('Name')}
							{...register('name')}
							error={fieldErrors.name}
						/>
						<PhoneNumberInput
							id="phone"
							variant="form"
							className="sm:col-span-2"
							control={control}
							defaultCountry={settingsCtx?.storeSettings?.address?.country as Country}
							label={t('Phone')}
							{...register('phone_number')}
							error={fieldErrors.phone_number}
						/>
						<InputGroup
							id="address"
							className="sm:col-span-2"
							label={t('Address')}
							{...register('address.address')}
							error={fieldErrors.address?.address}
						/>
						<InputGroup
							id="city"
							label={t('City')}
							{...register('address.city')}
							error={fieldErrors.address?.city}
						/>
						<InputGroup
							id="postal_code"
							label={t('Postal code')}
							{...register('address.postal_code')}
							error={fieldErrors.address?.postal_code}
						/>
					</ProfileSection>

					<Button
						type="submit"
						isLoading={isEditProfileLoading}
						className="h-10 w-full rounded-md px-8 text-sm font-medium shadow hover:bg-primary/90">
						{t('Update')}
					</Button>
				</form>
				<SecurityCard passwordReset={false} passwordChangeLink="password-change" />
			</div>
		</>
	)
}

export default ProfilePage
